import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
  Chip,
  Divider,
  Alert,
  Breadcrumbs,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CookieIcon from '@mui/icons-material/Cookie';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DeleteIcon from '@mui/icons-material/Delete';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';

const CookiePolicy = () => {
  const [cookiesList, setCookiesList] = useState([]);

  useEffect(() => {
    // Récupérer la liste des cookies actuels pour affichage
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key] = cookie.trim().split('=');
      if (key && !key.startsWith('_')) { // Filtrer les cookies techniques
        acc.push({
          nom: key,
          type: getCookieType(key),
          finalité: getCookiePurpose(key),
          durée: getCookieDuration(key),
          propriétaire: 'Site'
        });
      }
      return acc;
    }, []);
    setCookiesList(cookies);
  }, []);

  const getCookieType = (key) => {
    if (key.includes('essential')) return 'Essentiel';
    if (key.includes('preferences')) return 'Préférences';
    if (key.includes('analytics')) return 'Analytique';
    if (key.includes('consent')) return 'Consentement';
    return 'Fonctionnel';
  };

  const getCookiePurpose = (key) => {
    if (key.includes('essential')) return 'Assurer le fonctionnement du site';
    if (key.includes('preferences')) return 'Mémoriser vos préférences';
    if (key.includes('analytics')) return 'Analyser l\'utilisation du site';
    if (key.includes('consent')) return 'Enregistrer vos choix de cookies';
    return 'Fonctionnalité spécifique';
  };

  const getCookieDuration = (key) => {
    if (key.includes('displayed')) return '1 an';
    if (key.includes('consent')) return '1 an';
    if (key.includes('essential')) return 'Session';
    return 'Variable';
  };

  const handleClearCookies = () => {
    // On prévient AVANT d'effacer : `localStorage.clear()` emporte aussi le
    // panier en cours et la session de connexion. Sans cet avertissement, un
    // visiteur perdait sa commande sans comprendre pourquoi.
    const confirme = window.confirm(
      "Cela effacera vos préférences de cookies, ainsi que le contenu de votre panier " +
      "et votre session si vous êtes connecté.\n\n" +
      "Le bandeau de consentement vous sera de nouveau proposé.\n\nContinuer ?"
    );
    if (!confirme) return;

    // Suppression des cookies. On tente les deux portées : sans domaine (celle
    // utilisée à l'écriture) et avec, car un cookie ne s'efface qu'avec le même
    // couple chemin/domaine que celui qui l'a créé.
    const domain = window.location.hostname;
    document.cookie.split(';').forEach((cookie) => {
      const [key] = cookie.trim().split('=');
      if (!key) return;
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
    });

    localStorage.clear();

    window.location.reload();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Fil d'Ariane */}
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link href="/" color="inherit" underline="hover">
          Accueil
        </Link>
        <Typography color="text.primary">Politique de Cookies</Typography>
      </Breadcrumbs>

      {/* En-tête */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <CookieIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Politique de Cookies
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
          Dernière mise à jour : 03/03/2026
        </Typography>
      </Box>

      {/* Résumé */}
      <Alert 
        severity="info" 
        icon={<PrivacyTipIcon />}
        sx={{ mb: 4, borderRadius: 2 }}
      >
        <Typography variant="body1">
          <strong>Transparence et respect de votre vie privée</strong> — Cette politique explique 
          comment nous utilisons les cookies et comment vous pouvez les contrôler, conformément 
          au RGPD et à la CNIL.
        </Typography>
      </Alert>

      {/* Accordéon pour les sections */}
      <Box sx={{ mb: 6 }}>
        <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SecurityIcon color="primary" />
              <Typography variant="h6">Qu'est-ce qu'un cookie ?</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%', backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Définition technique
                  </Typography>
                  <Typography paragraph>
                    Un cookie est un petit fichier texte (généralement de 4 Ko) stocké sur votre 
                    appareil (ordinateur, tablette, smartphone) lorsque vous visitez un site web. 
                    Il contient des informations permettant au site de vous reconnaître lors de 
                    visites ultérieures.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les cookies sont totalement passifs — ils ne contiennent ni virus, ni logiciels 
                    espions, ni programmes malveillants.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: '100%', backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    À quoi servent-ils ?
                  </Typography>
                  <Typography paragraph>
                    Les cookies permettent d'améliorer votre expérience utilisateur en :
                  </Typography>
                  <ul style={{ paddingLeft: '20px', marginTop: '0' }}>
                    <li><Typography>Mémorisant vos préférences</Typography></li>
                    <li><Typography>Personnalisant le contenu</Typography></li>
                    <li><Typography>Analysant l'audience du site</Typography></li>
                    <li><Typography>Permettant le fonctionnement de fonctionnalités avancées</Typography></li>
                  </ul>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 2, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SettingsIcon color="primary" />
              <Typography variant="h6">Types de cookies utilisés</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {[
                {
                  title: 'Cookies Essentiels',
                  icon: <SecurityIcon color="success" />,
                  color: 'success',
                  description: 'Indispensables au fonctionnement du site. Ils ne peuvent pas être désactivés.',
                  examples: ['Authentification', 'Sécurité', 'Panier d\'achat']
                },
                {
                  title: 'Cookies de Préférences',
                  icon: <SettingsIcon color="warning" />,
                  color: 'warning',
                  description: 'Mémorisent vos choix pour personnaliser votre expérience.',
                  examples: ['Langue', 'Thème', 'Région']
                },
                {
                  title: 'Cookies Analytiques',
                  icon: <AnalyticsIcon color="info" />,
                  color: 'info',
                  description: 'Nous aident à comprendre comment le site est utilisé pour l\'améliorer.',
                  examples: ['Pages visitées', 'Temps passé', 'Erreurs techniques']
                }
              ].map((type, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Paper sx={{ 
                    p: 3, 
                    height: '100%',
                    borderLeft: `4px solid`,
                    borderLeftColor: `${type.color}.main`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                      {type.icon}
                      <Typography variant="h6">{type.title}</Typography>
                    </Box>
                    <Chip 
                      label={type.color === 'success' ? 'Toujours activé' : 'Optionnel'} 
                      color={type.color}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    <Typography paragraph variant="body2">
                      {type.description}
                    </Typography>
                    <Typography variant="subtitle2" gutterBottom>
                      Exemples :
                    </Typography>
                    <ul style={{ paddingLeft: '20px', marginTop: 0 }}>
                      {type.examples.map((ex, i) => (
                        <li key={i}><Typography variant="body2">{ex}</Typography></li>
                      ))}
                    </ul>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Précision d'honnêteté : le consentement analytique est prévu et
                fonctionnel, mais aucun outil de mesure d'audience n'est
                effectivement chargé sur le site à ce jour. */}
            <Alert severity="success" sx={{ mt: 3 }}>
              <strong>À ce jour, aucun outil de mesure d'audience n'est actif sur le site.</strong>{' '}
              Nous ne suivons pas vos pages visitées et ne partageons rien avec une régie
              publicitaire. La catégorie ci-dessus reste présente afin que votre choix soit
              déjà enregistré si un tel outil devait être ajouté un jour.
            </Alert>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 2, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CookieIcon color="primary" />
              <Typography variant="h6">Cookies actuellement utilisés</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {cookiesList.length > 0 ? (
              <>
                <Typography paragraph>
                  Voici la liste des cookies actuellement utilisés sur ce site :
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Nom du cookie</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Finalité</strong></TableCell>
                        <TableCell><strong>Durée</strong></TableCell>
                        <TableCell><strong>Propriétaire</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cookiesList.map((cookie, index) => (
                        <TableRow key={index}>
                          <TableCell>{cookie.nom}</TableCell>
                          <TableCell>
                            <Chip 
                              label={cookie.type} 
                              size="small"
                              color={
                                cookie.type === 'Essentiel' ? 'success' :
                                cookie.type === 'Préférences' ? 'warning' :
                                cookie.type === 'Analytique' ? 'info' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>{cookie.finalité}</TableCell>
                          <TableCell>{cookie.durée}</TableCell>
                          <TableCell>{cookie.propriétaire}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleClearCookies}
                  >
                    Supprimer tous mes cookies
                  </Button>
                </Box>
              </>
            ) : (
              <Typography>Aucun cookie n'est actuellement stocké sur votre appareil.</Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Stockage local : ces données ne sont pas des cookies au sens strict —
            elles ne sont jamais envoyées automatiquement au serveur — mais elles
            restent enregistrées sur l'appareil du visiteur et doivent donc être
            déclarées ici au même titre. */}
        <Accordion sx={{ mb: 2, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SettingsIcon color="primary" />
              <Typography variant="h6">Données conservées par votre navigateur</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 3 }}>
              En plus des cookies, le site enregistre quelques informations dans la mémoire
              locale de votre navigateur. Contrairement aux cookies, elles ne sont
              <strong> jamais transmises automatiquement</strong> à nos serveurs : elles
              restent sur votre appareil et servent uniquement à vous éviter de tout ressaisir.
            </Alert>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Information</strong></TableCell>
                    <TableCell><strong>À quoi elle sert</strong></TableCell>
                    <TableCell><strong>Durée</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    {
                      nom: 'Votre panier',
                      role: "Conserver les formations ajoutées et les informations d'inscription saisies, pour que vous puissiez quitter la page et revenir sans tout recommencer.",
                      duree: "Jusqu'au paiement ou au vidage du panier",
                    },
                    {
                      nom: 'Vos choix de cookies',
                      role: 'Se souvenir des préférences indiquées dans le bandeau, afin de ne plus vous les redemander.',
                      duree: '1 an',
                    },
                    {
                      nom: 'Conversation avec l\'assistant',
                      role: "Réafficher l'historique de la discussion en cours et limiter le nombre de messages par heure.",
                      duree: "Jusqu'à la fermeture de la conversation",
                    },
                    {
                      nom: 'Sondage déjà répondu',
                      role: 'Éviter de vous présenter deux fois le même sondage de satisfaction.',
                      duree: 'Jusqu\'à la suppression des données du site',
                    },
                    {
                      nom: 'Session de connexion',
                      role: "Vous garder connecté à votre espace, si vous disposez d'un compte. Le jeton expire automatiquement.",
                      duree: '24 heures',
                    },
                  ].map((item, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{item.nom}</TableCell>
                      <TableCell>{item.role}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.duree}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
              Aucune de ces informations n'est utilisée pour la publicité ni transmise à un tiers.
              Vous pouvez toutes les effacer en supprimant les données de site dans les réglages
              de votre navigateur.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PrivacyTipIcon color="primary" />
              <Typography variant="h6">Gestion de vos cookies</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" gutterBottom>
                    Navigateurs web
                  </Typography>
                  <Typography paragraph>
                    Chaque navigateur propose des paramètres pour gérer les cookies :
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <li>
                      <Link href="https://support.google.com/chrome/answer/95647" target="_blank">
                        Google Chrome
                      </Link>
                    </li>
                    <li>
                      <Link href="https://support.mozilla.org/fr/kb/cookies-informations-sites-enregistrent" target="_blank">
                        Mozilla Firefox
                      </Link>
                    </li>
                    <li>
                      <Link href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank">
                        Safari
                      </Link>
                    </li>
                    <li>
                      <Link href="https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank">
                        Microsoft Edge
                      </Link>
                    </li>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" gutterBottom>
                    Sur notre site
                  </Typography>
                  <Typography paragraph>
                    Lors de votre première visite, un bandeau vous propose d'accepter ou de
                    refuser. Votre réponse est conservée un an, et le bandeau ne réapparaît
                    plus ensuite.
                  </Typography>
                  <Typography paragraph>
                    Pour <strong>revenir sur votre choix</strong>, utilisez le bouton ci-dessous :
                    il efface les informations enregistrées par le site et le bandeau vous sera
                    de nouveau proposé au rechargement de la page.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DeleteIcon />}
                    sx={{ mt: 1 }}
                    onClick={handleClearCookies}
                  >
                    Revenir sur mon choix
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Section juridique */}
      <Paper sx={{ p: 4, mt: 4, backgroundColor: '#f5f5f5' }}>
        <Typography variant="h5" gutterBottom>
          ⚖️ Conformité légale
        </Typography>
        <Typography paragraph>
          Notre politique de cookies est conforme aux réglementations suivantes :
        </Typography>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, backgroundColor: 'white' }}>
              <Typography variant="h6" color="primary" gutterBottom>
                RGPD (UE 2016/679)
              </Typography>
              <Typography variant="body2">
                Règlement Général sur la Protection des Données — Consentement explicite requis 
                pour les cookies non-essentiels.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, backgroundColor: 'white' }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Loi Informatique et Libertés
              </Typography>
              <Typography variant="body2">
                Loi française modifiée — Droit d'accès, de rectification et d'opposition 
                au traitement de vos données.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Contact */}
      <Box sx={{ mt: 6, p: 4, textAlign: 'center', backgroundColor: 'primary.light', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
          Des questions sur notre politique de cookies ?
        </Typography>
        <Typography sx={{ color: 'white', mb: 2 }}>
          Contactez notre Délégué à la Protection des Données (DPD)
        </Typography>
        <Button 
          variant="contained" 
          href="/contact"
          sx={{ 
            backgroundColor: 'white', 
            color: 'primary.main',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }}
        >
          Nous contacter
        </Button>
      </Box>

      {/* Pied de page */}
      <Divider sx={{ my: 4 }} />
      <Typography variant="body2" color="text.secondary" align="center">
        Cette politique de cookies est régulièrement mise à jour. 
        Nous vous recommandons de la consulter périodiquement.
      </Typography>
    </Container>
  );
};

export default CookiePolicy;